import { useState, useCallback } from 'react';
import { Download, RefreshCw, Pencil, Check, X, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useImportJobRecords,
  useRetryImport,
  useUpdateStagingRow,
  createExportImportErrors,
} from '@/hooks/queries';
import type { ImportJobHistoryDto, ImportJobRecordDto } from '@signalvote/shared';

interface ImportErrorReviewProps {
  job: ImportJobHistoryDto;
  open: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 10;

export function ImportErrorReview({ job, open, onClose }: ImportErrorReviewProps) {
  const [page, setPage] = useState(1);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  const { data: records, isLoading } = useImportJobRecords(job.id, {
    status: 'Error',
    page,
    pageSize: PAGE_SIZE,
  });

  const retryMutation = useRetryImport();
  const { mutate: retryImport } = retryMutation;
  const updateStagingRowMutation = useUpdateStagingRow();
  const { mutateAsync: updateStagingRowAsync } = updateStagingRowMutation;
  const exportErrors = createExportImportErrors(job.id);

  const handleExportCsv = useCallback(async () => {
    try {
      await exportErrors();
    } catch (err) {
      console.error('Failed to export errors:', err);
    }
  }, [exportErrors]);

  const handleRetryAll = useCallback(() => {
    if (!records?.items) return;
    const rowNumbers = records.items.map((r) => r.rowNumber);
    retryImport({ jobId: job.id, rowNumbers });
  }, [records, retryImport, job.id]);

  const handleStartEdit = useCallback((record: ImportJobRecordDto) => {
    setEditingRowId(record.id);
    const values: Record<string, string> = {};
    if (record.rawData) {
      Object.entries(record.rawData).forEach(([key, val]) => {
        if (key !== 'id' && key !== 'imported_at') {
          values[key] = String(val ?? '');
        }
      });
    }
    setEditValues(values);
  }, []);

  const handleSaveAndRetry = useCallback(
    async (record: ImportJobRecordDto) => {
      try {
        await updateStagingRowAsync({
          tableName: record.stagingTableName,
          rowId: record.stagingRowId,
          data: editValues,
        });
        retryImport({ jobId: job.id, rowNumbers: [record.rowNumber] });
        setEditingRowId(null);
        setEditValues({});
      } catch (err) {
        console.error('Failed to save and retry:', err);
      }
    },
    [editValues, updateStagingRowAsync, retryImport, job.id]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingRowId(null);
    setEditValues({});
  }, []);

  const totalPages = records ? Math.ceil(records.totalCount / PAGE_SIZE) : 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Error Review</DialogTitle>
          <DialogDescription>
            Review and fix errors from the import on {formatDate(job.startedAt)}
          </DialogDescription>
        </DialogHeader>

        {/* Summary bar */}
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
          <div className="flex gap-4 text-sm">
            <span>{job.totalRecords} total</span>
            <span className="text-success">{job.importedCount} imported</span>
            {(job.linkedCount ?? 0) > 0 && <span className="text-info">{job.linkedCount} linked</span>}
            <span className="text-warning">{job.skippedCount} skipped</span>
            <span className="text-destructive">{job.errorCount} errors</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleExportCsv}>
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
            <Button
              size="sm"
              onClick={handleRetryAll}
              disabled={retryMutation.isPending || !records?.items?.length}
            >
              <RefreshCw
                className={cn('h-4 w-4 mr-1', retryMutation.isPending && 'animate-spin')}
              />
              Retry All Errors
            </Button>
          </div>
        </div>

        {/* Error records table */}
        {isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : records?.items?.length ? (
          <div className="space-y-2">
            {records.items.map((record) => (
              <div key={record.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">Row {record.rowNumber}</span>
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded-full text-xs font-medium',
                          record.status === 'Error'
                            ? 'bg-destructive/10 text-destructive'
                            : record.status === 'Imported'
                              ? 'bg-success/10 text-success'
                              : 'bg-warning/10 text-warning'
                        )}
                      >
                        {record.status}
                      </span>
                    </div>
                    {record.errorMessage && (
                      <p className="text-xs text-destructive">{record.errorMessage}</p>
                    )}
                  </div>
                  {record.status === 'Error' && editingRowId !== record.id && (
                    <Button size="sm" variant="ghost" onClick={() => handleStartEdit(record)}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                  )}
                </div>

                {/* Raw data display or edit mode */}
                {editingRowId === record.id ? (
                  <div className="space-y-2 bg-muted/30 rounded p-2">
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(editValues).map(([key, value]) => (
                        <div key={key} className="space-y-1">
                          <label className="text-xs text-muted-foreground">{key}</label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) =>
                              setEditValues((prev) => ({ ...prev, [key]: e.target.value }))
                            }
                            className="w-full px-2 py-1 text-sm border rounded bg-background"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                        <X className="h-3.5 w-3.5 mr-1" /> Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSaveAndRetry(record)}
                        disabled={updateStagingRowMutation.isPending}
                      >
                        <Check className="h-3.5 w-3.5 mr-1" /> Save & Retry
                      </Button>
                    </div>
                  </div>
                ) : record.rawData ? (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {Object.entries(record.rawData)
                      .filter(([key]) => key !== 'id' && key !== 'imported_at')
                      .slice(0, 6)
                      .map(([key, val]) => (
                        <span key={key}>
                          <span className="font-medium">{key}:</span> {String(val ?? '-')}
                        </span>
                      ))}
                    {Object.keys(record.rawData).length > 8 && (
                      <span className="italic">
                        +{Object.keys(record.rawData).length - 8} more
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
            ))}

            {/* Pagination */}
            {records.totalCount > PAGE_SIZE && (
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No error records found. All errors may have been resolved.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
